import {
  experimentsConfig,
  HIDE_SERVICE_INTERNAL_ID,
  HIDE_RETURN_INTERNAL_ID,
  DISABLE_COMPARISONS_INTERNAL_ID,
  ENABLE_STICKY_PRODUCT_INFO_INTERNAL_ID,
} from 'utils/config-experiments';
import { createSelector } from 'reselect';
import { getExperimentsDomain, getHostname, getPathname } from 'common/selectors';
import { useConfigurableExperiment } from '@cft/react-experiments';
import { getDomainFromHeader } from 'entries/server/utils';
import { RootState } from 'typesafe-actions';
import { createFeatureSelector } from 'features/create-feature-selector';
import {
  BOOSTER_EXPERIMENT,
  TINT_EXPERIMENT,
} from '../product-info/components/pdp-select-tint-color/pdp-select-tint-color.const';
import {
  ENABLE_SERVICES_DISCOUNT,
  HIDE_BOX_M2_SUGGEST_ID,
  LAMINATE_SAVE_PROJECT_EXPERIMENT,
  REVIEWS_FORM_HASH,
} from 'common/constants';

export const rootSelector = createFeatureSelector('pageModel');
export const getModelJson = createSelector(rootSelector, (r) => r?.model?.modelJson);
const mainSelector = (state: RootState) => state;

export const getBreadcrumbs = createSelector(getModelJson, (m) => m?.breadcrumbs);

export const getExperiments = createSelector(getModelJson, (m) => m?.experiments || []);
export const createGetExperiment = (experimentInternalID: string) =>
  createSelector(
    getExperiments,
    (experiments) => experiments?.find(({ internalId }) => internalId === experimentInternalID) ?? null,
  );
export const getExperimentsUUID = createSelector(mainSelector, (store: RootState) => store.experimentsUUID);

export type ABOnReturnType = {
  on: boolean;
  variant: string | null;
  name: string;
};

export const createGetExperimentOn = (experimentName: string) =>
  createSelector(
    [getExperimentsUUID, createGetExperiment(experimentName), getHostname, getExperimentsDomain],
    (uuid, experimentFromFudzi, host, experimentsDomain): ABOnReturnType => {
      const { variant, isActive } = experimentFromFudzi
        ? useConfigurableExperiment(
            experimentName,
            uuid,
            experimentsConfig,
            getDomainFromHeader(experimentsDomain || host),
          )
        : { variant: null, isActive: false };

      return {
        on: isActive,
        variant,
        name: experimentName,
      };
    },
  );

export const getIsTintServiceAvailableInRegion = createSelector(getExperiments, (experiments) => {
  return experiments.some((experiment) => experiment.internalId === TINT_EXPERIMENT);
});

export const getAuthUrl = createSelector(getModelJson, getPathname, (m, pathname) => {
  const authUrl = m?.header.initUrl || '';
  if (!authUrl) return '';
  if (!pathname) return authUrl;
  try {
    const url = new URL(authUrl);
    const state = url.searchParams.get('state');
    if (!state) return authUrl;
    const stateUrl = new URL(state);
    const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`;
    stateUrl.pathname = normalized;
    stateUrl.hash = REVIEWS_FORM_HASH;
    url.searchParams.set('state', stateUrl.toString());
    return url.toString();
  } catch {
    return authUrl;
  }
});
export const getHost = createSelector(
  ({ host }: RootState) => host,
  (s) => s,
);

export const getIsPdpBoosterExperiment = createSelector(getExperiments, (experiments) => {
  return experiments.some((experiment) => experiment.internalId === BOOSTER_EXPERIMENT);
});

export const getIsEnableServicesDiscount = createSelector(getExperiments, (experiments) => {
  return experiments.some((experiment) => experiment.internalId === ENABLE_SERVICES_DISCOUNT);
});

export const getHideServices = createGetExperimentOn(HIDE_SERVICE_INTERNAL_ID);

export const getHideReturn = createGetExperimentOn(HIDE_RETURN_INTERNAL_ID);

export const getAllowComparisons = createSelector(getExperiments, (experiments) => {
  // HACK: isDisable конвертится в isAllow для упрощения, чтобы не фиксить тесты
  const isDisable = experiments.some(
    (experiment) => experiment.internalId === DISABLE_COMPARISONS_INTERNAL_ID,
  );

  return !isDisable;
});

export const getHideBoxM2Suggest = createSelector(getExperiments, (experiments) =>
  experiments.some(({ internalId }) => internalId === HIDE_BOX_M2_SUGGEST_ID),
);

export const getDivision = createSelector(getModelJson, (model) => model?.breadcrumbs[2]?.label ?? null);

export const getSubdivision = createSelector(getModelJson, (model) => model?.breadcrumbs[3]?.label ?? null);

export const getCatalogue4LevelLink = createSelector(
  getModelJson,
  (model) => model?.breadcrumbs[4]?.link ?? '',
);

export const getFamily = createSelector(getModelJson, (model) =>
  !model?.properties?.familyId ? null : model.properties.familyId,
);

export const getYaSplitInfo = createSelector(getModelJson, (model) => model?.yaSplitInfo ?? null);

export const getEnableStickyProductInfo = createSelector(getExperiments, (experiments) =>
  experiments.some(({ internalId }) => internalId === ENABLE_STICKY_PRODUCT_INFO_INTERNAL_ID),
);

export const getEnableLaminateSaveProject = createSelector(getExperiments, (experiments) =>
  experiments.some(({ internalId }) => internalId === LAMINATE_SAVE_PROJECT_EXPERIMENT),
);

export const getOriginalProduct = createSelector(getModelJson, (model) => model?.originalProduct);

export const getFAQ = createSelector(getModelJson, (model) => model?.faq);

export const getUniqueOffer = createSelector(getModelJson, (model) => model?.uniqueOffer);
